import { z } from "zod";
import { ProposalType } from "../../types";

export const unitDetailsSchema = z.object({
  clientNameSnapshot: z.string().min(1, "Client name is required*"),
  compoundNameSnapshot: z.string().min(1, "Compound name is required*"),
  propertyTypeNameSnapshot: z.string().min(1, "Property type is required*"),
  unitAreaSnapshot: z.coerce
    .number()
    .positive("Unit area must be a positive number*"),
  pricePerMeter: z.coerce
    .number()
    .nonnegative("Price per meter cannot be negative*"),
  finishingCost: z.coerce
    .number()
    .nonnegative("Finishing cost cannot be negative*"),
  furnishingCost: z.coerce
    .number()
    .nonnegative("Furnishing cost cannot be negative*"),
});

const paymentPlanSchema = z.object({
  percentage: z.coerce
    .number()
    .min(0, "Payment plan percentage cannot be negative*")
    .max(100, "Payment plan percentage cannot exceed 100*"),
  amount: z.coerce.number().nonnegative("Amount cannot be negative*"),
});

const emptyToUndefined = (value: unknown) =>
  value === null || value === "" ? undefined : value;

export const financialDetailsSchema = z
  .object({
    proposalType: z.nativeEnum(ProposalType).default(ProposalType.Normal),
    finishingMonths: z.coerce
      .number()
      .int()
      .positive("Finishing months must be a positive integer*"),
    loanAmount: z.preprocess(
      emptyToUndefined,
      z.coerce.number().int().nonnegative().optional(),
    ),
    loanTerm: z.preprocess(
      emptyToUndefined,
      z.coerce.number().int().positive().optional(),
    ),
    monthlyInstallment: z.preprocess(
      emptyToUndefined,
      z.coerce.number().int().nonnegative().optional(),
    ),
    rentalAmount: z.coerce
      .number()
      .positive("Rental amount must be a positive number*"),
    annualIncreasePercent: z.coerce
      .number()
      .min(0, "Annual increase cannot be negative*")
      .max(100, "Annual increase cannot exceed 100*"),
    rentalYears: z.coerce
      .number()
      .int()
      .positive("Number of years must be a positive integer*"),
    clientCostSharePercent: z.coerce
      .number()
      .min(0, "Cost share cannot be negative*")
      .max(100, "Cost share cannot exceed 100*"),
    clientRevenueSharePercent: z.coerce
      .number()
      .min(0, "Revenue share cannot be negative*")
      .max(100, "Revenue share cannot exceed 100*"),
    paymentPlan: z.array(paymentPlanSchema).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.proposalType === ProposalType.Normal) {
      if (!data.paymentPlan || data.paymentPlan.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Payment plan cannot be empty",
          path: ["paymentPlan"],
        });
      } else {
        const sum = data.paymentPlan
          .map((p) => p.percentage)
          .reduce((a, b) => a + b, 0);
        if (Math.abs(sum - 100) >= 0.01) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Total percentage of payment plan must equal 100%",
            path: ["paymentPlan"],
          });
        }
      }
    }
    if (data.proposalType === ProposalType.NawyNow) {
      if (data.loanAmount == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Loan amount is required for NawyNow proposals*",
          path: ["loanAmount"],
        });
      }
      if (data.loanTerm == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Loan term is required for NawyNow proposals*",
          path: ["loanTerm"],
        });
      }
      if (data.monthlyInstallment == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Monthly installment is required for NawyNow proposals*",
          path: ["monthlyInstallment"],
        });
      }
    }
  });

export const proposalDetailsSchema = z.object({
  proposalInformation: z
    .array(z.string())
    .min(1, "Proposal information must not be empty"),
  mainSpecs: z
    .record(z.array(z.string()))
    .refine((obj) => Object.keys(obj).length > 0, {
      message: "mainSpecs must not be empty",
    }),
  extraSpecs: z.record(z.array(z.string())),
});
